import { type PaginationProps } from 'antd';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useGetProductItemsListsQuery,
  useLazyGetProductItemsSearchResultQuery,
  useSearchProductItemsMutation,
} from '@app/api/Products';
import type {
  GetProductItemsSearchResultRequest,
  ModelProductItem,
  ModelProductItemsSearchFilter,
  ModelProductItemsSearchResult,
  SearchProductItemsRequest,
} from '@app/api/Products';
import { EDIT_PRODUCT_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import {
  initialFilters,
  getColumns,
  initPagination,
} from '@app/pages/Warehouses/Products/data';
import type {
  FilterChangeType,
  ProductsFilters,
  Pagination,
} from '@app/pages/Warehouses/Products/types';
import {
  writeSession,
  clearSession,
  readSession,
  getWarehouseIdFromSearchParams,
  WAREHOUSE_ID_SEARCH_PARAM,
} from '@app/pages/Warehouses/Products/utils';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';

import type { DefaultOptionType } from 'antd/es/select';
import type { ChangeEventHandler } from 'react';

export const useProducts = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const {
    userAccessRights: {
      Products: { IsOn },
      Warehouse: { IsProductItemsDelete },
    },
  } = useNewUserAccessRights();

  const {
    data: filtersLists,
    isFetching: isFiltersListsFetching,
    isLoading: isFiltersListsLoading,
    isUninitialized: isFiltersListsUninitialized,
    isError: isFiltersListsError,
    error: filtersListsError,
  } = useGetProductItemsListsQuery(
    { ActiveBranchId },
    {
      skip: !ActiveBranchId || !IsOn,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );

  useErrorHandler([{ flag: isFiltersListsError, error: filtersListsError }]);

  const isPageForbidden = useMemo(() => !IsOn, [IsOn]);

  const [searchProductItems, { isLoading, isUninitialized }] =
    useSearchProductItemsMutation();

  const [searchProductItemsData, setSearchProductItemsData] =
    useState<ModelProductItemsSearchResult | null>(null);

  const searchIdRef = useRef<number | null>(null);
  const lastFiltersRef = useRef<ProductsFilters | null>(null);
  const lastBranchRef = useRef(ActiveBranchId);
  const initialWarehouseIdSearchParamRef = useRef(
    getWarehouseIdFromSearchParams(searchParams)
  );
  const [getProductItemsSearchResult, { isLoading: isLoadingById }] =
    useLazyGetProductItemsSearchResultQuery();

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [deleteModalData, setDeleteModalData] =
    useState<ModelProductItem | null>(null);
  const [isOpenEditSalePriceModal, setIsOpenEditSalePriceModal] =
    useState<boolean>(false);
  const [editSalePriceData, setEditSalePriceData] =
    useState<ModelProductItem | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<DefaultOptionType[]>(
    []
  );
  const [brandsOptions, setBrandsOptions] = useState<DefaultOptionType[]>([]);
  const [warehousesOptions, setWarehousesOptions] = useState<
    DefaultOptionType[]
  >([]);

  const [filters, setFilters] = useState<ProductsFilters>(() => ({
    ...initialFilters,
  }));
  const [unsavedFilters, setUnsavedFilters] = useState<ProductsFilters>(() => ({
    ...initialFilters,
  }));

  const [pagination, setPagination] = useState<Pagination>(
    () => initPagination
  );
  const { PageN, ItemsPerPage } = pagination;

  const defaultWarehouse =
    filtersLists?.Warehouses?.find(({ IdS }) => IdS === 'primary')?.Id ?? null;

  const onSaveFiltersToStorage = useCallback(() => {
    const filtersToSave: ModelProductItemsSearchFilter = {
      ...filters,
      PageN,
      ItemsPerPage,
    };
    writeSession(filtersToSave);
  }, [filters, PageN, ItemsPerPage]);

  const selectedWarehouseId = unsavedFilters.WarehouseId;

  const syncWarehouseIdSearchParam = useCallback(() => {
    if (
      selectedWarehouseId === null ||
      !Number.isInteger(selectedWarehouseId) ||
      selectedWarehouseId <= 0
    ) {
      return;
    }

    setSearchParams(
      (prevSearchParams) => {
        const nextSearchParams = new URLSearchParams(prevSearchParams);
        const nextWarehouseId = String(selectedWarehouseId);

        if (
          nextSearchParams.get(WAREHOUSE_ID_SEARCH_PARAM) === nextWarehouseId
        ) {
          return prevSearchParams;
        }

        nextSearchParams.set(WAREHOUSE_ID_SEARCH_PARAM, nextWarehouseId);

        return nextSearchParams;
      },
      { replace: true }
    );
  }, [selectedWarehouseId, setSearchParams]);

  const onFilterChange: FilterChangeType = useCallback((key, value) => {
    setUnsavedFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onChangeSearchTerm: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { value: SearchTerm = null } }) => {
      setUnsavedFilters((prev) => ({ ...prev, SearchTerm }));
    },
    []
  );

  const skipFilters = useCallback(() => {
    const emptyFilters = {
      ...initialFilters,
      WarehouseId: defaultWarehouse,
    };
    setUnsavedFilters(emptyFilters);
    setFilters(emptyFilters);
    clearSession();
  }, [defaultWarehouse]);

  const onSearch = useCallback(() => {
    if (!unsavedFilters.WarehouseId) {
      showNotification('Пожалуйста, выберите склад для поиска', 'warning');
      return;
    }
    setPagination((prev) => ({ ...prev, PageN: 1 }));
    setFilters(unsavedFilters);
  }, [showNotification, unsavedFilters]);

  const onPaginate: PaginationProps['onChange'] = useCallback(
    (PageN: number) => {
      setPagination((prev) => ({ ...prev, PageN }));
    },
    []
  );

  const onPageSizeChange = useCallback((ItemsPerPage: number) => {
    setPagination((prev) => ({ ...prev, ItemsPerPage, PageN: 1 }));
  }, []);

  const isPageLoading = useMemo(
    () => (isLoading || isLoadingById) && !isUninitialized,
    [isLoading, isLoadingById, isUninitialized]
  );
  const isFetching = useMemo(
    () => isLoading || isLoadingById,
    [isLoading, isLoadingById]
  );

  const disabledFilters = useMemo(() => {
    return (
      isFiltersListsFetching ||
      isFiltersListsLoading ||
      isFiltersListsUninitialized ||
      isFetching ||
      isPageLoading
    );
  }, [
    isFiltersListsFetching,
    isFiltersListsLoading,
    isFiltersListsUninitialized,
    isFetching,
    isPageLoading,
  ]);

  const searchProductItemsHandler = useCallback(async () => {
    const data: SearchProductItemsRequest['data'] = {
      ...filters,
      ItemsPerPage,
      PageN,
      PriceOfSaleE: filters?.PriceOfSaleE,
      PriceOfSaleS: filters?.PriceOfSaleS,
      PriceOfPurchaseE: filters?.PriceOfPurchaseE,
      PriceOfPurchaseS: filters?.PriceOfPurchaseS,
    };

    try {
      const response = await searchProductItems({
        data,
        ActiveBranchId,
      }).unwrap();

      setSearchProductItemsData(response);

      searchIdRef.current = response.Id;
      lastFiltersRef.current = filters;
      lastBranchRef.current = ActiveBranchId;

      const { CountProducts, PageN } = response;
      setPagination((prev) => ({ ...prev, Total: CountProducts, PageN }));
    } catch (error) {
      showNotification(error, 'error');
    }
  }, [
    ActiveBranchId,
    searchProductItems,
    showNotification,
    filters,
    ItemsPerPage,
    PageN,
  ]);

  const getProductItemsSearchResultHandler = useCallback(
    async (params: GetProductItemsSearchResultRequest) => {
      try {
        const response = await getProductItemsSearchResult(params).unwrap();
        setSearchProductItemsData(response);
        const { CountProducts, PageN } = response;
        setPagination((prev) => ({ ...prev, Total: CountProducts, PageN }));
      } catch (error) {
        showNotification(error, 'error');
      }
    },
    [getProductItemsSearchResult, showNotification]
  );

  const onSearchHandler = useCallback(async () => {
    if (!filtersLists) return;
    if (!filters.WarehouseId) return;

    const filtersChanged =
      !lastFiltersRef.current || !isEqual(filters, lastFiltersRef.current);

    const branchChanged = ActiveBranchId !== lastBranchRef.current;
    const searchId = searchIdRef.current;

    const isNewSearch = !searchId || filtersChanged || branchChanged;

    if (isNewSearch) {
      await searchProductItemsHandler();
    } else {
      if (!searchId) {
        showNotification('Не удалось найти идентификатор поиска', 'error');
        return;
      }

      await getProductItemsSearchResultHandler({
        searchId: searchId,
        pageN: PageN,
        itemsPerPage: ItemsPerPage,
        ActiveBranchId,
      });
    }
  }, [
    filters,
    ActiveBranchId,
    searchProductItemsHandler,
    getProductItemsSearchResultHandler,
    showNotification,
    PageN,
    ItemsPerPage,
    filtersLists,
  ]);

  const tableData = useMemo(() => {
    if (!searchProductItemsData) return [];
    const { Products } = searchProductItemsData;

    if (!Products || !Products.length) return [];

    return Products;
  }, [searchProductItemsData]);

  const onEdit = useCallback(
    (id: number) => {
      navigate(`${EDIT_PRODUCT_PATH}?id=${id}`);
    },
    [navigate]
  );

  const onOpenGoodsUnits = useCallback(
    (id: number) => {
      navigate(
        `/warehouses/goods-units?Products=${id}&warehouseId=${filters.WarehouseId}`
      );
    },
    [filters.WarehouseId, navigate]
  );

  const openDeleteModal = useCallback((product: ModelProductItem) => {
    setIsOpenDeleteModal(true);
    setDeleteModalData(product);
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    setIsOpenDeleteModal(false);
    setDeleteModalData(null);
  }, []);

  const openEditSalePriceModal = useCallback((product: ModelProductItem) => {
    setEditSalePriceData(product);
    setIsOpenEditSalePriceModal(true);
  }, []);

  const onCloseEditSalePriceModal = useCallback(() => {
    setIsOpenEditSalePriceModal(false);
    setEditSalePriceData(null);
  }, []);

  const columns = useMemo(
    () =>
      getColumns({
        onEdit,
        onEditSalePrice: openEditSalePriceModal,
        onOpenGoodsUnits,
        onDelete: openDeleteModal,
        showDelete: IsProductItemsDelete,
      }),
    [
      onEdit,
      openEditSalePriceModal,
      onOpenGoodsUnits,
      openDeleteModal,
      IsProductItemsDelete,
    ]
  );

  const filtersListsHandler = useCallback(() => {
    if (!filtersLists) return;

    const { Categories, Brands, Warehouses } = filtersLists;
    const categoryOptions: DefaultOptionType[] = Categories.map(
      ({ Name, Id }) => ({ label: Name, value: Id })
    );
    const brandsOptions: DefaultOptionType[] = Brands.map(({ Name, Id }) => ({
      label: Name,
      value: Id,
    }));

    const warehousesOptions: DefaultOptionType[] = Warehouses?.map(
      ({ Name, Id }) => ({ label: Name, value: Id })
    );

    setCategoryOptions(categoryOptions);
    setBrandsOptions(brandsOptions);
    setWarehousesOptions(warehousesOptions);
    const filtersFromStorage = readSession();
    const warehouseIdFromSearchParams =
      initialWarehouseIdSearchParamRef.current;
    const selectedWarehouseId =
      warehouseIdFromSearchParams !== null &&
      Warehouses.some(({ Id }) => Id === warehouseIdFromSearchParams)
        ? warehouseIdFromSearchParams
        : filtersFromStorage?.WarehouseId || defaultWarehouse;

    if (filtersFromStorage) {
      const filters: ProductsFilters = {
        WarehouseId: selectedWarehouseId,
        SearchTerm: filtersFromStorage.SearchTerm,
        CategoryIds: filtersFromStorage.CategoryIds,
        BrandIds: filtersFromStorage.BrandIds,
        PriceOfPurchaseS: filtersFromStorage.PriceOfPurchaseS,
        PriceOfPurchaseE: filtersFromStorage.PriceOfPurchaseE,
        PriceOfSaleS: filtersFromStorage.PriceOfSaleS,
        PriceOfSaleE: filtersFromStorage.PriceOfSaleE,
        IsAtMinimal: filtersFromStorage.IsAtMinimal,
        IsAtCritical: filtersFromStorage.IsAtCritical,
      };
      setUnsavedFilters(filters);
      setFilters(filters);
      setPagination((prev) => ({
        ...prev,
        PageN: filtersFromStorage.PageN,
        ItemsPerPage: filtersFromStorage.ItemsPerPage,
      }));
    } else {
      setUnsavedFilters((prev) => ({
        ...prev,
        WarehouseId: selectedWarehouseId,
      }));
      setFilters((prev) => ({ ...prev, WarehouseId: selectedWarehouseId }));
    }
  }, [defaultWarehouse, filtersLists]);

  return {
    onFilterChange,
    isPageLoading,
    isFetching,
    onChangeSearchTerm,
    tableData,
    skipFilters,
    onSearch,
    onPageSizeChange,
    onPaginate,
    pagination,
    unsavedFilters,
    onSearchHandler,
    columns,
    disabledFilters,
    categoryOptions,
    brandsOptions,
    warehousesOptions,
    filtersListsHandler,
    syncWarehouseIdSearchParam,
    isPageForbidden,
    onCloseDeleteModal,
    isOpenDeleteModal,
    deleteModalData,
    onSaveFiltersToStorage,
    isOpenEditSalePriceModal,
    editSalePriceData,
    onCloseEditSalePriceModal,
  };
};
